import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { clearTryOnItems, setActiveItems } from '../../redux/slices/clothingItemsSlice';
import { selectVTOResult, selectSelectedVTOResultId } from '../../redux/slices/vtoSlice';

const useVirtualTryOnInteractions = ({
  isVirtualMode,
  isAvailable,
  personImage,
  toggleMode,
  tryOnGarment,
  clearTryOnError,
  virtualTryOnResults,
  setVirtualTryOnResults,
  droppedItems,
  setDroppedItems,
  placeholderWidth,
  placeholderHeight,
  // New modal handlers
  showProcessingModal,
  hideProcessingModal,
  showErrorModal,
  // Person image state for updating with VTO results
  personImageState,
  // Active person image ID (passed separately to avoid spreading issues)
  activePersonImageId,
  // Active person image data (for useRemovedImage flag)
  activePersonImage,
  // Callback for when VTO completes
  onVTOComplete,
  // Redux state and dispatch for multi-item support
  activeItems = [],
  dispatch
}) => {
  // Get selected VTO result ID from Redux instead of local state
  const selectedVTOResultId = useSelector(selectSelectedVTOResultId);
  const handleManualPositioning = (item) => {
    try {
      if (!item || !item.image) {
        console.warn('Invalid item for manual positioning:', item);
        return;
      }

      const img = document.createElement('img');
      // Use removed bg image if useRemovedImage is true and it exists
      const displayImageUrl = (item.useRemovedImage && item.removedBgImage?.url) 
        ? item.removedBgImage.url 
        : (item.image?.url || item.image);
      img.src = displayImageUrl;

      img.onload = () => {
        try {
          let aspectRatio = img.height / img.width;

          // Validate aspect ratio
          if (!isFinite(aspectRatio) || aspectRatio <= 0) {
            console.warn('Invalid aspect ratio for item:', item);
            aspectRatio = 1; // Default to square
          }

          const newWidth = Math.max(50, placeholderWidth / 1.5);
          const newHeight = newWidth * aspectRatio;

          const x = Math.max(0, (placeholderWidth - newWidth) / 2);
          const y = Math.max(0, (placeholderHeight - newHeight) / 2);

          const newItem = {
            ...item,
            // Override image with the display URL for canvas rendering
            image: displayImageUrl,
            x,
            y,
            width: newWidth,
            height: newHeight,
            rotation: 0,
            zIndex: Math.max(0, ...droppedItems.map(item => item.zIndex || 0)) + 1,
            isVirtualTryOn: false
          };

          setDroppedItems(prevItems => [...prevItems, newItem]);
        } catch (error) {
          console.error('Error in image onload for manual positioning:', error);
        }
      };

      img.onerror = () => {
        console.error('Failed to load image for manual positioning:', displayImageUrl);
        // Still add the item with default dimensions
        const newItem = {
          ...item,
          image: displayImageUrl,
          x: (placeholderWidth - 100) / 2,
          y: (placeholderHeight - 100) / 2,
          width: 100,
          height: 100,
          rotation: 0,
          zIndex: Math.max(0, ...droppedItems.map(item => item.zIndex || 0)) + 1,
          isVirtualTryOn: false
        };
        setDroppedItems(prevItems => [...prevItems, newItem]);
      };
    } catch (error) {
      console.error('Error in manual positioning:', error);
    }
  };

  // Clear all wearing items and revert to original person image
  const handleClearAllWearing = useCallback(() => {
    // Clear selected VTO result ID
    dispatch(selectVTOResult(null));
    console.log('✅ Cleared selected VTO result ID');

    if (dispatch) {
      dispatch(clearTryOnItems());
      console.log('✅ Cleared all wearing items');
    }

    // Revert person image to original, respecting useRemovedImage flag
    if (personImageState && activePersonImage) {
      // Determine which version of the person image to show based on useRemovedImage flag
      const useRemovedImageFlag = activePersonImage.useRemovedImage !== false;
      let imageToShow;

      console.log('🔍 CLEAR ALL - Person Image Data:', {
        personImageId: activePersonImage.id,
        useRemovedImageFlag,
        hasRemovedBgImage: !!activePersonImage.removedBgImage?.url,
        hasOriginalImage: !!activePersonImage.image?.url
      });

      if (useRemovedImageFlag && activePersonImage.removedBgImage?.url) {
        // Use background-removed version
        imageToShow = activePersonImage.removedBgImage.url;
        console.log('✅ Reverting to background-removed person image (useRemovedImage=true)');
      } else if (activePersonImage.image?.url) {
        // Use original version with background
        imageToShow = activePersonImage.image.url;
        console.log('✅ Reverting to original person image with background (useRemovedImage=false)');
      } else {
        // Fallback to any available URL
        imageToShow = activePersonImage.imageUrl || activePersonImage.url;
        console.log('⚠️ Using fallback person image URL for revert');
      }

      // Use setVirtualTryOnResult to set the person image (not a VTO result)
      if (imageToShow && personImageState.setVirtualTryOnResult) {
        personImageState.setVirtualTryOnResult({
          image: imageToShow,
          originalImage: null, // No original since this IS the original
          metadata: {
            isVirtualTryOnResult: false, // This is the person image, not a VTO result
            originalPersonImage: imageToShow
          }
        });
      } else if (personImageState.revertToOriginal) {
        // Fallback to revertToOriginal if setVirtualTryOnResult is not available
        personImageState.revertToOriginal();
      }
    } else if (personImageState && personImageState.revertToOriginal) {
      // Fallback if activePersonImage is not available
      personImageState.revertToOriginal();
      console.log('✅ Reverted to original person image (fallback)');
    }
  }, [dispatch, personImageState, activePersonImage]);

  // Remove a single item from the outfit
  const handleRemoveItem = useCallback(async (itemToRemove) => {
    try {
      console.log('🗑️ Removing item:', {
        itemId: itemToRemove.id,
        itemName: itemToRemove.clothesName || itemToRemove.name,
        currentActiveItems: activeItems.length
      });

      // Show processing modal with pending credit status
      if (showProcessingModal) {
        showProcessingModal('Updating your look...', 10, null, false);
      }

      // Calculate remaining items
      const remainingItems = activeItems.filter(item => item.id !== itemToRemove.id);

      // Edge case: removing last item - revert to original person image
      if (remainingItems.length === 0) {
        console.log('🔄 Last item removed, reverting to original person image');
        handleClearAllWearing();
        if (hideProcessingModal) {
          hideProcessingModal();
        }
        return;
      }

      // Get remaining item IDs
      const remainingItemIds = remainingItems.map(item => item.id);

      // Import VTO service
      const VirtualTryOnService = (await import('../../services/virtualTryOnService')).default;

      const baseImageSource = {
        type: 'person_image',
        id: activePersonImageId
      };

      // Step 1: Check cache first (fast)
      if (showProcessingModal) {
        showProcessingModal('Checking cache...', 20, null, false);
      }

      const cacheCheck = await VirtualTryOnService.checkCacheOnly({
        baseImageSource,
        clothingItemIds: remainingItemIds
      });

      const useRemovedImageFlag = activePersonImage?.useRemovedImage !== false;

      // If cached, use cached result directly
      if (cacheCheck.cached) {
        if (showProcessingModal) {
          showProcessingModal('Loading cached result...', 80, 0, true);
        }

        const result = {
          success: true,
          cached: true,
          imageUrl: Array.isArray(cacheCheck.result.resultImage) ? (cacheCheck.result.resultImage[0]?.url || cacheCheck.result.resultImage[0]) : cacheCheck.result.resultImage,
          removedBgImage: cacheCheck.result.removedBgImage,
          savedResultId: cacheCheck.result.id,
          metadata: cacheCheck.result.generationMetadata
        };

        // Update selected VTO result
        if (result.savedResultId) {
          dispatch(selectVTOResult(result.savedResultId));
        }

        // Update active items
        dispatch(setActiveItems(remainingItems));

        // Update canvas
        const displayImage = (useRemovedImageFlag && result.removedBgImage)
          ? result.removedBgImage
          : result.imageUrl;

        if (personImageState && personImageState.setVirtualTryOnResult) {
          personImageState.setVirtualTryOnResult({
            image: displayImage,
            originalImage: personImage,
            metadata: {
              isVirtualTryOnResult: true,
              allClothingItemIds: remainingItemIds,
              layeredItems: remainingItems,
              cached: true,
              backendSaved: true,
              removedItemId: itemToRemove.id
            }
          });
        }

        if (showProcessingModal) {
          showProcessingModal('Loaded from cache!', 100, 0, true);
        }

        setTimeout(() => {
          if (hideProcessingModal) {
            hideProcessingModal();
          }
        }, 500);

        if (onVTOComplete) {
          onVTOComplete(result, itemToRemove, 'remove');
        }

        return;
      }

      // Step 2: Not cached - generate new result
      if (showProcessingModal) {
        showProcessingModal('Generating new look...', 50, 1, false);
      }

      // Call backend to generate VTO result for remaining items
      const result = await VirtualTryOnService.getOrGenerateVTOForCombination({
        baseImageSource,
        clothingItemIds: remainingItemIds,
        useRemovedImage: useRemovedImageFlag,
        options: {
          operation: 'remove',
          removedItemId: itemToRemove.id
        }
      });

      if (result && result.success) {
        if (showProcessingModal) {
          showProcessingModal('Complete!', 100, 1, false);
        }

        // Update selected VTO result ID
        if (result.savedResultId) {
          dispatch(selectVTOResult(result.savedResultId));
        }

        // Update active items in Redux
        dispatch(setActiveItems(remainingItems));

        // Update canvas with new result
        const displayImage = (useRemovedImageFlag && result.removedBgImage)
          ? result.removedBgImage
          : result.imageUrl;

        if (personImageState && personImageState.setVirtualTryOnResult) {
          personImageState.setVirtualTryOnResult({
            image: displayImage,
            originalImage: personImage,
            metadata: {
              isVirtualTryOnResult: true,
              allClothingItemIds: remainingItemIds,
              layeredItems: remainingItems,
              watermarkedImage: Array.isArray(result.watermarkedImage || result.backendResult?.watermarkedImage) ? (result.watermarkedImage || result.backendResult?.watermarkedImage)[0]?.url : (result.watermarkedImage || result.backendResult?.watermarkedImage)?.url,
              cached: result.cached,
              backendSaved: result.backendSaved,
              removedItemId: itemToRemove.id
            }
          });
        }

        setTimeout(() => {
          if (hideProcessingModal) {
            hideProcessingModal();
          }
        }, 500);

        // Trigger callback to update UI
        if (onVTOComplete) {
          onVTOComplete(result, itemToRemove, 'remove');
        }
      } else {
        throw new Error('Failed to retrieve or generate VTO result');
      }
    } catch (error) {
      console.error('[VTO] Remove item failed:', error);
      if (hideProcessingModal) {
        hideProcessingModal();
      }
      if (showErrorModal) {
        showErrorModal(
          'Failed to remove item. Please try again.',
          () => handleRemoveItem(itemToRemove)
        );
      }
    }
  }, [activeItems, activePersonImageId, activePersonImage, dispatch, personImageState, personImage, showProcessingModal, hideProcessingModal, showErrorModal, onVTOComplete, handleClearAllWearing, selectedVTOResultId]);

  // Method to trigger VTO using all items currently on the canvas
  const generateVTOFromCanvasItems = useCallback(async (itemsToTryOn) => {
    if (!itemsToTryOn || itemsToTryOn.length === 0) {
      console.warn('No items to try on');
      return;
    }

    try {
      clearTryOnError();

      // Ensure we have a person image
      const hasPersonImage = personImage || personImageState?.displayProps?.personImage;
      if (!hasPersonImage) {
        if (showErrorModal) {
          showErrorModal('Please upload a person image first to use Virtual Try On.');
        }
        return;
      }

      if (showProcessingModal) {
        showProcessingModal('Creating your virtual try-on from canvas items...', 10, null, false);
      }

      const VirtualTryOnService = (await import('../../services/virtualTryOnService')).default;

      // Ensure items have IDs and image URLs
      const clothingItemIds = itemsToTryOn.map(item => item.id).filter(Boolean);
      
      if (clothingItemIds.length === 0) {
        throw new Error('Could not identify valid clothing items on canvas.');
      }

      // Check cache first
      if (showProcessingModal) {
        showProcessingModal('Checking cache...', 20, null, false);
      }

      const cacheCheck = await VirtualTryOnService.checkCacheOnly({
        baseImageSource: { type: 'person_image', id: activePersonImageId },
        clothingItemIds: clothingItemIds
      });

      let result;

      if (cacheCheck.cached) {
        if (showProcessingModal) {
          showProcessingModal('Loading cached result...', 80, 0, true);
        }
        
        result = {
          success: true,
          cached: true,
          imageUrl: Array.isArray(cacheCheck.result.resultImage) ? (cacheCheck.result.resultImage[0]?.url || cacheCheck.result.resultImage[0]) : cacheCheck.result.resultImage,
          removedBgImage: cacheCheck.result.removedBgImage,
          savedResultId: cacheCheck.result.id,
          metadata: cacheCheck.result.generationMetadata,
          creditBalance: cacheCheck.creditBalance,
          allClothingItemIds: clothingItemIds,
          layeredItems: itemsToTryOn
        };
      } else {
        if (showProcessingModal) {
          showProcessingModal('Generating new look...', 50, 1, false);
        }

        // We will call the backend for a combination of items
        const useRemovedImageFlag = activePersonImage?.useRemovedImage !== false;
        result = await VirtualTryOnService.getOrGenerateVTOForCombination({
          baseImageSource: { type: 'person_image', id: activePersonImageId },
          clothingItemIds: clothingItemIds,
          useRemovedImage: useRemovedImageFlag,
          options: {
            operation: 'combine',
            items: itemsToTryOn.map(item => ({
              id: item.id,
              garmentType: item.category,
              garmentName: item.clothesName || item.name,
              garmentImage: item.image?.url || item.image
            }))
          }
        });
      }

      if (result && result.success && (result.imageUrl || result.removedBgImage)) {
        if (showProcessingModal) {
          showProcessingModal('Complete!', 100, result.cached ? 0 : 1, result.cached);
        }

        // Switch to virtual mode
        if (toggleMode) {
          toggleMode(true);
        }

        if (result.savedResultId) {
          dispatch(selectVTOResult(result.savedResultId));
        }

        // Set active items in Redux
        dispatch(setActiveItems(itemsToTryOn));

        // Update person image
        if (personImageState && personImageState.setVirtualTryOnResult) {
          const useRemovedImageFlag = activePersonImage?.useRemovedImage !== false;
          const displayImage = (useRemovedImageFlag && result.removedBgImage) ? result.removedBgImage : result.imageUrl;

          personImageState.setVirtualTryOnResult({
            image: displayImage,
            originalImage: personImage,
            metadata: {
              ...result.metadata,
              isVirtualTryOnResult: true,
              allClothingItemIds: clothingItemIds,
              layeredItems: itemsToTryOn,
              watermarkedImage: Array.isArray(result.watermarkedImage || result.backendResult?.watermarkedImage) ? (result.watermarkedImage || result.backendResult?.watermarkedImage)[0]?.url : (result.watermarkedImage || result.backendResult?.watermarkedImage)?.url,
              originalPersonImage: personImage,
              backendSaved: result.backendSaved
            }
          });
        }
        
        // Remove individual items from canvas (they are now on the person image)
        setDroppedItems([]);

        setTimeout(() => {
          if (hideProcessingModal) hideProcessingModal();
        }, 500);

        if (onVTOComplete) {
          onVTOComplete(result, itemsToTryOn[0], 'combine');
        }
      } else {
        throw new Error('Virtual try-on result was invalid');
      }

    } catch (error) {
      console.error('Canvas VTO failed:', error);
      if (hideProcessingModal) hideProcessingModal();
      if (showErrorModal) {
        showErrorModal(error.message || 'Failed to generate virtual try-on');
      }
    }
  }, [
    clearTryOnError, personImage, personImageState, activePersonImageId,
    activePersonImage, showProcessingModal, hideProcessingModal, showErrorModal,
    dispatch, toggleMode, setDroppedItems, onVTOComplete
  ]);

  // Card click handler (for adding/removing items to/from canvas)
  const handleCardClick = useCallback(async (item, e, action = 'auto') => {
    if (!item || !item.image) {
      console.warn('Invalid item data for card click:', item);
      return;
    }

    try {
      clearTryOnError();

      // Determine if item is currently selected
      const isCurrentlySelected = activeItems.some(activeItem => activeItem.id === item.id);

      // Determine final action
      let finalAction = action;
      if (action === 'auto') {
        finalAction = isCurrentlySelected ? 'remove' : 'add';
      }

      console.log('[VTO] Card click:', {
        itemId: item.id,
        itemName: item.clothesName || item.name,
        isCurrentlySelected,
        action: finalAction
      });

      // If removing, call handleRemoveItem
      if (finalAction === 'remove') {
        await handleRemoveItem(item);
        return;
      }

      // Otherwise, proceed with add logic (existing code below)

      // Check if we have a person image (original or VTO result)
      const hasPersonImage = personImage ||
        personImageState?.displayProps?.personImage;

      if (isVirtualMode && hasPersonImage) {
        try {
          // Show processing modal with pending credit status
          if (showProcessingModal) {
            showProcessingModal('Preparing...', 10, null, false);
          }

          // Import VTO service for enhanced backend integration
          const VirtualTryOnService = (await import('../../services/virtualTryOnService')).default;

          // Get garment image URL (backend will handle URL-to-base64 conversion)
          const garmentImageUrl = item.image?.url || item.image;

          // Build clothing item IDs for cache check
          const isVTOResultOnCanvas = personImageState?.displayProps?.metadata?.isVirtualTryOnResult === true;
          let currentlyWornItemIds = [];
          
          if (activeItems && activeItems.length > 0) {
            currentlyWornItemIds = activeItems.map(i => i.id);
          } else if (isVTOResultOnCanvas && personImageState?.displayProps?.metadata?.allClothingItemIds) {
            currentlyWornItemIds = personImageState.displayProps.metadata.allClothingItemIds;
          }

          const allClothingItemIds = [...currentlyWornItemIds, item.id];

          // Step 1: Check cache first (fast)
          if (showProcessingModal) {
            showProcessingModal('Checking cache...', 20, null, false);
          }

          const cacheCheck = await VirtualTryOnService.checkCacheOnly({
            baseImageSource: { type: 'person_image', id: activePersonImageId },
            clothingItemIds: allClothingItemIds
          });

          // If cached, show cached status immediately and return result
          if (cacheCheck.cached) {
            if (showProcessingModal) {
              showProcessingModal('Loading cached result...', 80, 0, true);
            }

            const result = {
              success: true,
              cached: true,
              imageUrl: Array.isArray(cacheCheck.result.resultImage) ? (cacheCheck.result.resultImage[0]?.url || cacheCheck.result.resultImage[0]) : cacheCheck.result.resultImage,
              removedBgImage: cacheCheck.result.removedBgImage,
              savedResultId: cacheCheck.result.id,
              metadata: cacheCheck.result.generationMetadata,
              creditBalance: cacheCheck.creditBalance
            };

            if (showProcessingModal) {
              showProcessingModal('Loaded from cache!', 100, 0, true);
            }

            // Update selected VTO result
            if (result.savedResultId) {
              dispatch(selectVTOResult(result.savedResultId));
            }

            // Update active items
            const newActiveItems = [...(activeItems || []), item];
            dispatch(setActiveItems(newActiveItems));

            // Update canvas
            const useRemovedImageFlag = activePersonImage?.useRemovedImage !== false;
            const displayImage = (useRemovedImageFlag && result.removedBgImage)
              ? result.removedBgImage
              : result.imageUrl;

            if (personImageState && personImageState.setVirtualTryOnResult) {
              personImageState.setVirtualTryOnResult({
                image: displayImage,
                originalImage: personImage,
                metadata: {
                  isVirtualTryOnResult: true,
                  allClothingItemIds,
                  layeredItems: newActiveItems,
                  watermarkedImage: Array.isArray(result.watermarkedImage || result.backendResult?.watermarkedImage) ? (result.watermarkedImage || result.backendResult?.watermarkedImage)[0]?.url : (result.watermarkedImage || result.backendResult?.watermarkedImage)?.url,
                  cached: true,
                  backendSaved: true
                }
              });
            }

            setTimeout(() => {
              if (hideProcessingModal) {
                hideProcessingModal();
              }
            }, 500);

            if (onVTOComplete) {
              onVTOComplete(result, item, 'add');
            }

            return;
          }

          // Step 2: Not cached - show generating status with credit deduction
          if (showProcessingModal) {
            showProcessingModal('Creating your virtual try-on...', 50, 1, false);
          }

          // Determine which person image to use:
          // - If there's a VTO result on canvas (check metadata), use current canvas image
          // - Otherwise, use original person image
          const hasWornItems = activeItems && activeItems.length > 0;

          let personImageToUse;
          if (isVTOResultOnCanvas || hasWornItems) {
            // Canvas is showing a VTO result, use it for layering
            personImageToUse = personImageState?.displayProps?.personImage || personImage;
          } else {
            // No VTO result on canvas, use original person image
            personImageToUse = personImage;
          }

          // currentlyWornItemIds and allClothingItemIds already set above from cache check

          // CRITICAL DEBUG LOG - This is what we're sending to backend
          console.log('[VTO] VTO REQUEST:', {
            newItemId: item.id,
            newItemName: item.clothesName || item.name,
            currentlyWornItemIds,
            allClothingItemIds,
            personImageId: activePersonImageId,
            isLayering: currentlyWornItemIds.length > 0,
            activeItemsInRedux: activeItems?.length || 0,
            activeItemsIds: activeItems?.map(i => i.id) || []
          });

          // Determine base image source based on selected VTO result from Redux
          console.log('[VTO] BASE IMAGE SOURCE DETERMINATION:', {
            selectedVTOResultId,
            activePersonImageId
          });

          let baseImageSource;
          if (selectedVTOResultId) {
            // Layering on a VTO result (from Redux state)
            baseImageSource = {
              type: 'vto_result',
              id: selectedVTOResultId
            };
            console.log('[VTO] USING VTO RESULT AS BASE:', baseImageSource);
          } else {
            // Starting fresh with person image
            baseImageSource = {
              type: 'person_image',
              id: activePersonImageId
            };
            console.log('[VTO] USING PERSON IMAGE AS BASE:', baseImageSource);
          }

          // Use enhanced try-on method that saves to backend with multi-item support
          const result = await VirtualTryOnService.tryOnGarmentWithBackendSave({
            baseImageSource, // Send ID instead of URL
            clothingItemId: item.id, // The new item being added
            garmentImageUrl: garmentImageUrl, // Send URL directly - backend will convert if needed
            currentlyWornItemIds: currentlyWornItemIds, // CRITICAL: Use the IDs we determined above
            options: {
              garmentType: item.category,
              garmentId: item.id,
              garmentName: item.clothesName || item.name,
              garmentImage: item.image?.url || item.image // Pass image URL for metadata
            },
            saveToBackend: true
          });

          // CRITICAL DEBUG LOG - This is what backend returned
          console.log('[VTO] VTO RESPONSE:', {
            success: result?.success,
            cached: result?.cached,
            backendSaved: result?.backendSaved,
            layeredItemsCount: result?.metadata?.layeredItems?.length || 0,
            layeredItemsIds: result?.metadata?.layeredItems?.map(i => i.id) || [],
            resultImageUrl: result?.imageUrl?.substring(0, 100)
          });

          // Show different message for cached vs new results
          if (showProcessingModal) {
            if (result?.cached) {
              showProcessingModal('Using cached result...', 90, 0, true);
            } else {
              showProcessingModal('Finalizing result...', 90, 1, false);
            }
          }

          console.log('[VTO] Virtual try-on result:', {
            success: result?.success,
            hasResultImage: !!result?.imageUrl,
            backendSaved: result?.backendSaved,
            metadata: result?.metadata
          });

          if (result && result.success && result.imageUrl) {
            if (showProcessingModal) {
              if (result.cached) {
                showProcessingModal('Loaded from cache!', 100, 0, true);
              } else {
                showProcessingModal('Complete!', 100, 1, false);
              }
            }

            // Set the new VTO result as selected for next layering
            if (result.savedResultId) {
              dispatch(selectVTOResult(result.savedResultId));
              console.log('[VTO] Set selected VTO result ID for next layering:', result.savedResultId);
            }

            // Hide processing modal after a brief delay
            setTimeout(() => {
              if (hideProcessingModal) {
                hideProcessingModal();
              }
            }, 500);

            // Check if this item already exists on canvas
            const existingIndex = droppedItems.findIndex(droppedItem =>
              droppedItem.id === item.id && droppedItem.isVirtualTryOn
            );

            if (existingIndex !== -1) {
              // Update existing virtual try-on item
              console.log('[VTO] Updating existing virtual try-on item');
              setDroppedItems(prevItems =>
                prevItems.map((prevItem, index) =>
                  index === existingIndex
                    ? {
                      ...prevItem,
                      image: result.imageUrl,
                      tryOnMetadata: result.metadata,
                      backendSaved: result.backendSaved
                    }
                    : prevItem
                ), 'virtual-tryon-update'
              );

              setVirtualTryOnResults(prev => {
                const filtered = prev.filter(r => r.garmentId !== item.id);
                const displayImage = result.removedBgImage || result.imageUrl;
                return [...filtered, {
                  garmentId: item.id,
                  resultImage: displayImage, // Use removedBgImage if available
                  metadata: result.metadata,
                  backendSaved: result.backendSaved,
                  backendResult: result.backendResult
                }];
              });
            } else {
              // Update person image with VTO result instead of adding as canvas item

              // Update person image in Redux with VTO result
              if (personImageState && personImageState.setVirtualTryOnResult) {
                // Respect the person image's useRemovedImage flag
                const useRemovedImageFlag = activePersonImage?.useRemovedImage !== false;
                let displayImage;

                if (useRemovedImageFlag && result.removedBgImage) {
                  // Use removed background version
                  displayImage = result.removedBgImage;
                  console.log('[VTO] VTO display: Using removed background (useRemovedImage=true)');
                } else {
                  // Use original version with background
                  displayImage = result.imageUrl;
                  console.log('[VTO] VTO display: Using original with background (useRemovedImage=false)');
                }

                console.log('[VTO] VTO display image:', {
                  hasRemovedBg: !!result.removedBgImage,
                  useRemovedImageFlag,
                  usingImage: displayImage?.substring(0, 100)
                });

                const watermarkObj = result.watermarkedImage || result.backendResult?.watermarkedImage;
                const watermarkUrl = Array.isArray(watermarkObj) ? watermarkObj[0]?.url : watermarkObj?.url;

                personImageState.setVirtualTryOnResult({
                  image: displayImage, // Respect useRemovedImage flag
                  originalImage: personImage, // CRITICAL: Set original image for layering to work
                  metadata: {
                    ...result.metadata,
                    isVirtualTryOnResult: true,
                    garmentId: item.id,
                    garmentName: item.clothesName || item.name,
                    allClothingItemIds: result.allClothingItemIds,
                    layeredItems: result.layeredItems,
                    isLayered: result.isLayered,
                    watermarkedImage: watermarkUrl,
                    originalPersonImage: personImage,
                    uploadTimestamp: Date.now(),
                    processingMethod: 'virtual-try-on',
                    backendSaved: result.backendSaved,
                    backendResult: result.backendResult
                  }
                });

                console.log('[VTO] Person image updated with VTO result (Cloudinary URL stored)');
              } else {
                console.warn('[VTO] personImageState.setVirtualTryOnResult not available, cannot update person image');
              }

              // Update wearing list in Redux
              if (dispatch) {
                // Determine wearing list from backend result
                let allItems = [];

                // Check multiple locations for layeredItems
                const layeredItems = result.metadata?.layeredItems ||
                  result.backendResult?.layeredItems ||
                  result.backendResult?.generationMetadata?.layeredItems;

                if (layeredItems && Array.isArray(layeredItems) && layeredItems.length > 0) {
                  // Map metadata items to the structure expected by the frontend
                  allItems = layeredItems
                    .sort((a, b) => (a.order || 0) - (b.order || 0))
                    .map(item => ({
                      ...item,
                      id: item.id,
                      name: item.name || item.clothesName || 'Unknown Item',
                      clothesName: item.name || item.clothesName || 'Unknown Item',
                      category: item.category || item.garmentType,
                      image: item.imageUrl || item.image,
                      imageUrl: item.imageUrl || item.image,
                      licensingEnabled: item.source_listing?.licensingEnabled || item.source_product?.licensingEnabled || item.licensingEnabled || false,
                      licensingType: item.source_listing?.licensingType || item.source_product?.licensingType || item.licensingType || null,
                      licensingPrice: item.source_listing?.licensingPrice || item.source_product?.licensingPrice || item.licensingPrice || null
                    }));
                } else if (result.backendResult?.closetClothes) {
                  // Ensure closetClothes is always treated as an array
                  const clothesArray = Array.isArray(result.backendResult.closetClothes)
                    ? result.backendResult.closetClothes
                    : [result.backendResult.closetClothes];

                  // Map to expected structure
                  allItems = clothesArray.map(item => ({
                    ...item,
                    id: item.id,
                    name: item.clothesName || item.name || 'Unknown Item',
                    clothesName: item.clothesName || item.name || 'Unknown Item',
                    category: item.category,
                    image: item.image?.url || item.image || item.imageUrl,
                    licensingEnabled: item.source_listing?.licensingEnabled || item.source_product?.licensingEnabled || item.licensingEnabled || false,
                    licensingType: item.source_listing?.licensingType || item.source_product?.licensingType || item.licensingType || null,
                    licensingPrice: item.source_listing?.licensingPrice || item.source_product?.licensingPrice || item.licensingPrice || null
                  }));
                }

                // CRITICAL DEBUG LOG - This is what we're setting as activeItems
                console.log('[VTO] SETTING ACTIVE ITEMS:', {
                  count: allItems.length,
                  itemIds: allItems.map(i => i.id),
                  itemNames: allItems.map(i => i.clothesName || i.name)
                });

                if (allItems.length > 0) {
                  dispatch(setActiveItems(allItems));
                } else {
                  console.warn('[VTO] No items found to set as active');
                }
              } else {
                console.error('[VTO] No dispatch available to update wearing list');
              }

              setVirtualTryOnResults(prev => {
                const displayImage = result.removedBgImage || result.imageUrl;
                return [...prev, {
                  garmentId: item.id,
                  resultImage: displayImage, // Use removedBgImage if available
                  metadata: result.metadata,
                  backendSaved: result.backendSaved,
                  backendResult: result.backendResult
                }];
              });

              // Trigger callback to refetch VTO results and update UI
              if (onVTOComplete) {
                onVTOComplete(result, item);
              }

              console.log('[VTO] Virtual try-on result processed - person image updated');
            }
          } else {
            console.warn('Virtual try-on result invalid, falling back to manual positioning');
            if (hideProcessingModal) {
              hideProcessingModal();
            }
            if (showErrorModal) {
              showErrorModal(
                'Virtual try-on result was invalid',
                null,
                () => handleManualPositioning(item)
              );
            } else {
              handleManualPositioning(item);
            }
          }
        } catch (error) {
          console.error('Virtual try-on failed:', error);

          if (hideProcessingModal) {
            hideProcessingModal();
          }

          if (showErrorModal) {
            const retryAction = () => {
              // Retry the same action
              handleCardClick(item, e);
            };

            const manualAction = () => {
              handleManualPositioning(item);
            };

            showErrorModal(error, retryAction, manualAction);
          } else {
            // Fallback - should not happen as modals should always be available
            console.error('No error modal available, falling back to manual positioning');
            handleManualPositioning(item);
          }
        }
      } else if (isVirtualMode && !hasPersonImage) {
        console.log('[VTO] Virtual mode enabled but no person image available');

        if (showErrorModal) {
          const manualAction = () => {
            toggleMode();
            handleManualPositioning(item);
          };

          showErrorModal(
            'Virtual try-on requires a person image. Please upload a person image or switch to manual positioning mode.',
            null,
            manualAction
          );
        } else {
          // Fallback - should not happen as error modal should always be available
          console.warn('No error modal available for person image missing');
          toggleMode();
          handleManualPositioning(item);
        }
      } else {
        console.log('[VTO] Using manual positioning (not in virtual mode)');
        handleManualPositioning(item);
      }
    } catch (error) {
      console.error('Error in virtual try-on card click handler:', error);
      // Fallback to manual positioning
      handleManualPositioning(item);
    }
  }, [isVirtualMode, isAvailable, personImage, tryOnGarment, clearTryOnError, droppedItems, setDroppedItems, setVirtualTryOnResults, placeholderWidth, placeholderHeight, toggleMode, activeItems, dispatch, personImageState, showProcessingModal, hideProcessingModal, showErrorModal, onVTOComplete, handleRemoveItem, activePersonImage, activePersonImageId, selectedVTOResultId, handleManualPositioning]);

  // Virtual try-on specific handlers
  const handleRevertToOriginal = (index) => {
    const item = droppedItems[index];
    if (item.isVirtualTryOn && item.originalImage) {
      setDroppedItems(prevItems =>
        prevItems.map((prevItem, i) =>
          i === index
            ? {
              ...prevItem,
              image: prevItem.originalImage,
              isVirtualTryOn: false,
              originalImage: undefined,
              tryOnMetadata: undefined
            }
            : prevItem
        )
      );

      setVirtualTryOnResults(prev =>
        prev.filter(result => result.garmentId !== item.id)
      );
    }
  };

  const handleRetryVirtualTryOn = async (index) => {
    const item = droppedItems[index];
    if (item && personImage) {
      try {
        clearTryOnError();

        // Get garment image URL (backend will handle URL-to-base64 conversion)
        const garmentImageUrl = item.originalImage || item.image?.url || item.image;

        // Import VTO service for enhanced backend integration
        const VirtualTryOnService = (await import('../../services/virtualTryOnService')).default;

        const result = await VirtualTryOnService.tryOnGarmentWithBackendSave({
          personImageId: activePersonImageId,
          originalPersonImageId: activePersonImageId, // CRITICAL: For backend person_image relation
          personImageUrl: personImage, // Send URL directly - backend will convert if needed
          clothingItemId: item.id,
          garmentImageUrl: garmentImageUrl, // Send URL directly - backend will convert if needed
          options: {
            garmentType: item.category || 'upper_body',
            garmentId: item.id
          },
          saveToBackend: true
        });

        if (result && result.success) {
          setDroppedItems(prevItems =>
            prevItems.map((prevItem, i) => {
              if (i === index) {
                const displayImage = result.removedBgImage || result.imageUrl;
                return {
                  ...prevItem,
                  image: displayImage, // Use removedBgImage if available
                  isVirtualTryOn: true,
                  originalImage: prevItem.originalImage || prevItem.image,
                  tryOnMetadata: result.metadata,
                  backendSaved: result.backendSaved
                };
              }
              return prevItem;
            })
          );

          setVirtualTryOnResults(prev => {
            const filtered = prev.filter(r => r.garmentId !== item.id);
            const displayImage = result.removedBgImage || result.imageUrl;
            return [...filtered, {
              garmentId: item.id,
              resultImage: displayImage, // Use removedBgImage if available
              metadata: result.metadata,
              backendSaved: result.backendSaved,
              backendResult: result.backendResult
            }];
          });
        }
      } catch (error) {
        console.error('Retry virtual try-on failed:', error);
      }
    }
  };



  return {
    handleCardClick,
    handleRevertToOriginal,
    handleRetryVirtualTryOn,
    handleClearAllWearing,
    handleRemoveItem,
    generateVTOFromCanvasItems,
    selectedVTOResultId
  };
};

export default useVirtualTryOnInteractions;